// Managers (MANAGERS.md, scenarios J4 and J3): who runs each team once a
// league has drafted, and — for the commissioner — replacing one, putting a
// bot on a team nobody runs, and nudging a manager whose lineup has a slot
// that cannot score this week. Remove a manager and the team stands with
// nobody; send the team's link and whoever opens it takes it over as it
// stands. Every member reads it; before the draft the pre-draft page is the
// same list with that stage's actions.
//
// Read from `GET /api/leagues/:id/setup`, which every member may read and
// which carries a team's link only to the people who may send it; and, for
// the commissioner, `GET /api/leagues/:id/lineups`, every team's lineup this
// week at a glance.

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LeagueLineupsView, LeagueSetupView, valueOf, type SeatRef } from '@fsp/domain';
import { messageOf } from '../data/api';
import { isDemoLeague } from '../data/demo';
import { exportLeague } from '../data/exportFile';
import { useLeagues } from '../data/LeaguesProvider';
import { leagueAction } from '../data/leagueSetup';
import { useLeagueView } from '../data/leagueSource';
import { thenRefresh } from '../data/moves';
import { Panel } from '../kit/Panel';
import { Button, IconButton, Stack, Text, Title, tell } from '../kit/ui';
import { useEditTeam } from '../shell/editTeam';
import '../styles/screens/predraft.css';
import { FromLeague } from './shared/FromLeague';
import { NoCommissioner, SeatRow, YouPanel } from './shared/Seats';
import { BotSheet, NudgeSheet, RemoveManagerSheet, SeatInviteSheet } from './shared/SeatSheets';

const SIM_AS_OF = new Date(0).toISOString();

export function ManagersScreen() {
  const { leagueId } = useParams();
  const path = leagueId ? `/leagues/${encodeURIComponent(leagueId)}/setup` : null;
  const snapshot = useLeagueView(path, LeagueSetupView, null, SIM_AS_OF);
  return (
    <FromLeague snapshot={snapshot}>
      {(view) => <Managers view={view} leagueId={leagueId ?? view.league.id} />}
    </FromLeague>
  );
}

type Open = { kind: 'remove' | 'invite' | 'bot' | 'nudge'; teamId: string } | null;

function Managers({ view: read, leagueId }: { view: LeagueSetupView; leagueId: string }) {
  // THE SEATS AS THE LAST ACT LEFT THEM, until the screen's own read is newer:
  // the invite sheet opened after a removal read "Dave runs Dave's Packers
  // now" until the refresh landed, and kept saying it if the refresh failed
  // (the review of J4).
  const [acted, setActed] = useState<LeagueSetupView | null>(null);
  const view = acted !== null && acted.updatedAt > read.updatedAt ? acted : read;
  // EVERY TEAM'S LINEUP THIS WEEK, for the commissioner only: the flag on a
  // row and the nudge (J3). Held the same way after a nudge.
  const lineupsPath = read.canManage ? `/leagues/${encodeURIComponent(leagueId)}/lineups` : null;
  const lineupsRead = valueOf(useLeagueView(lineupsPath, LeagueLineupsView, null, SIM_AS_OF));
  const [nudged, setNudged] = useState<LeagueLineupsView | null>(null);
  const lineups =
    nudged !== null && (lineupsRead === null || nudged.updatedAt > lineupsRead.updatedAt)
      ? nudged
      : lineupsRead;
  const glanceOf = (teamId: string) => lineups?.teams.find((t) => t.teamId === teamId) ?? null;
  const editTeam = useEditTeam();
  const leagues = useLeagues();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<Open>(null);
  const run = view.seats.filter((s) => s.seat === 'manager').length;

  /** The league's own acts — the vote, handing over, leaving — and what they say. */
  const act = async (path: string, body: unknown, done: string) => {
    setBusy(true);
    const result = await thenRefresh(leagueId, leagueAction(leagueId, path, body));
    setBusy(false);
    if (result.status !== 'ok') {
      tell(
        messageOf(result, 'That did not work.', 'Could not reach the site. Try again.'),
        result.status === 'refused' ? 'warn' : 'bad',
      );
      return;
    }
    tell(done, 'good');
    // Somebody who left is not in the league any more: back to the hub.
    if (path === '/leave') {
      leagues.reload();
      navigate('/');
    }
  };

  // EACH TEAM AS A FILE (A13, PLAN-2026-10-18 §13.1): any member's, for any
  // team, beside the league's own on the League page's office.
  const [exporting, setExporting] = useState<string | null>(null);
  const exportTeam = (seat: SeatRef) => {
    setExporting(seat.teamId);
    void exportLeague(view.league.id, seat.teamId).then((done) => {
      setExporting(null);
      tell(done.ok ? `Saved ${done.name}.` : done.message, done.ok ? 'good' : 'bad');
    });
  };

  const actions = (seat: SeatRef) => {
    // The demo league's visitor is nobody to export as.
    const save = isDemoLeague(view.league.id) ? null : (
      <IconButton
        icon="download"
        label={`Export ${seat.name}`}
        variant="secondary"
        disabled={exporting !== null}
        onClick={() => exportTeam(seat)}
      />
    );
    const edit = seat.isMine ? (
      <IconButton icon="edit" label="Edit team" variant="secondary" onClick={editTeam.open} />
    ) : null;
    const remove =
      view.canManage && seat.seat === 'manager' && !seat.isMine && seat.role !== 'commissioner' ? (
        <Button size="xs" onClick={() => setOpen({ kind: 'remove', teamId: seat.teamId })}>
          Remove
        </Button>
      ) : null;
    // TODO(J6): the commissioner's Hand back to <name> for a team its last
    // manager asked for, a link only they can use (MANAGERS.md "Coming back").
    const invite =
      view.canManage && seat.ownerName === null ? (
        <Button
          size="xs"
          variant={seat.invite !== null ? 'soft' : 'secondary'}
          onClick={() => setOpen({ kind: 'invite', teamId: seat.teamId })}
        >
          {seat.invite !== null ? 'Link' : 'Invite'}
        </Button>
      ) : null;
    const bot =
      view.canManage && seat.ownerName === null ? (
        <Button
          size="xs"
          variant={seat.seat === 'bot' ? 'soft' : 'secondary'}
          onClick={() => setOpen({ kind: 'bot', teamId: seat.teamId })}
        >
          Bot
        </Button>
      ) : null;
    const glance = glanceOf(seat.teamId);
    const nudge =
      glance !== null && (glance.canNudge || glance.nudgedAt !== null) ? (
        <Button
          size="xs"
          variant={glance.canNudge ? 'secondary' : 'soft'}
          onClick={() => setOpen({ kind: 'nudge', teamId: seat.teamId })}
        >
          {glance.canNudge ? 'Nudge' : 'Nudged'}
        </Button>
      ) : null;
    return edit || remove || invite || bot || nudge || save ? (
      <div className="fsp-predraft-seat-actions">
        {edit}
        {nudge}
        {remove}
        {invite}
        {bot}
        {save}
      </div>
    ) : null;
  };

  /** This week's trouble on a team, for the commissioner: the count, never a name to start. */
  const note = (seat: SeatRef) => {
    const glance = glanceOf(seat.teamId);
    if (glance === null || glance.health.status !== 'error') return null;
    return (
      <Text size="xs" tone="warn" nowrap block>
        {glance.health.headline}
        {glance.nudgedAt !== null ? ' · nudged' : ''}
      </Text>
    );
  };
  const nudging =
    open?.kind === 'nudge' ? (view.seats.find((s) => s.teamId === open.teamId) ?? null) : null;

  return (
    <div className="fsp-screen">
      <div className="fsp-widgets fsp-predraft-column">
        <Stack gap={4}>
          <div>
            <Title>Managers</Title>
            <Text tone="secondary" block>
              {view.league.name} · {view.seats.length} teams
            </Text>
          </div>

          {view.needsCommissioner ? (
            <NoCommissioner view={view} busy={busy} act={(p, b, d) => void act(p, b, d)} />
          ) : null}

          <Panel title="Teams" meta={`${run} of ${view.seats.length} with a manager`}>
            <Stack gap={2}>
              {view.canManage ? (
                <Text size="xs" tone="secondary" block>
                  To replace a manager, remove them, then send the team’s invite link to whoever is
                  taking over. The team keeps its roster and its record. A team whose lineup has a
                  slot that can’t score{' '}
                  {lineups?.period ? `in ${lineups.period.label}` : 'this week'} says so here.
                </Text>
              ) : null}
              <div className="fsp-seats fsp-seats-roomy">
                {view.seats.map((seat) => (
                  <SeatRow
                    key={seat.teamId}
                    seat={seat}
                    actions={actions(seat)}
                    openLabel="No manager"
                    note={note(seat)}
                    claimable={false}
                  />
                ))}
              </div>
            </Stack>
          </Panel>

          <YouPanel view={view} busy={busy} act={(p, b, d) => void act(p, b, d)} drafted />
        </Stack>
      </div>
      <RemoveManagerSheet
        view={view}
        leagueId={leagueId}
        teamId={open?.kind === 'remove' ? open.teamId : null}
        onClose={() => setOpen(null)}
        onRemoved={(teamId) => setOpen({ kind: 'invite', teamId })}
        onView={setActed}
      />
      <SeatInviteSheet
        view={view}
        leagueId={leagueId}
        teamId={open?.kind === 'invite' ? open.teamId : null}
        onClose={() => setOpen(null)}
        onView={setActed}
      />
      <BotSheet
        view={view}
        leagueId={leagueId}
        teamId={open?.kind === 'bot' ? open.teamId : null}
        onClose={() => setOpen(null)}
        onView={setActed}
      />
      <NudgeSheet
        seat={nudging}
        glance={nudging === null ? null : glanceOf(nudging.teamId)}
        period={lineups?.period?.label ?? null}
        leagueId={leagueId}
        onClose={() => setOpen(null)}
        onGlance={setNudged}
      />
    </div>
  );
}
